import { useCallback, useMemo } from "react";
import { useQueryState } from "nuqs";
import { LIST_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { createUrlFilterParser, type UrlFilterParserOptions } from "@shared/utils/url/parsers/urlFilter";
import { callIfFn } from "@shared/utils/callIfFn";

/** Результат {@link useUrlFilter}. */
export interface UseUrlFilterReturn<T> {
    filter: T | null;
    setFilter: (newFilter: T | null | ((prev: T | null) => T | null), replace?: boolean) => Promise<URLSearchParams>;
    applyFilter: (newFilter: T | null | ((prev: T | null) => T | null), replace?: boolean) => void;
    rawParam: string | null;
}

/** Опции {@link useUrlFilter}. */
export interface UseUrlFilterOptions<T extends object = Record<string, unknown>> extends UrlFilterParserOptions<T> {
    paramKey?: string;
}

/**
 * Фильтр списка в URL (`flt` по умолчанию): чтение/запись через `nuqs`, остальные query-параметры не трогаются.
 */
export function useUrlFilter<T extends object = Record<string, unknown>>(options: UseUrlFilterOptions<T> = {}) {
    const { paramKey = LIST_FILTER_QUERY_PARAM, validate, defaultFilter = null } = options;
    const parser = useMemo(() => createUrlFilterParser<T>({ validate, defaultFilter }), [defaultFilter, validate]);
    const [filter, setFilterState] = useQueryState(paramKey, parser);

    const setFilter = useCallback<UseUrlFilterReturn<T>["setFilter"]>(
        (newFilter, replace = false) => {
            const actualFilter = callIfFn(newFilter, filter);
            return setFilterState(actualFilter, {
                history: replace ? "replace" : "push",
            });
        },
        [filter, setFilterState],
    );

    const applyFilter = useCallback<UseUrlFilterReturn<T>["applyFilter"]>(
        (newFilter, replace = false) => {
            void setFilter(newFilter, replace);
        },
        [setFilter],
    );

    return {
        filter,
        setFilter,
        applyFilter,
        rawParam: typeof window !== "undefined" ? new URLSearchParams(window.location.search).get(paramKey) : null,
    };
}
